/**
 * This file is meant for shared display components that you use throughout
 * your application.
 *
 * Components with a lot of logic, or those that are particularly complicated
 * should probably be put in their own file. This file is meant for the
 * re-usable, simple things used in a lot of different spots in your UI.
 */
import { Thread } from '@/models/Thread';

import { Snackbar } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import WarningOutlined from '@mui/icons-material/WarningOutlined';

export const Error = ({ message }: { message: string }) => (
  <ErrorGrid>
    <WarningOutlined />
    {message}
  </ErrorGrid>
);

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: auto max-content;
  grid-gap: ${({ theme }) => `${theme.spacing.xs}`};
  align-items: center;
`;

const ErrorGrid = styled(TwoColumnGrid)`
  color: ${({ theme }) => theme.palette.text.error.hex};
`;

export const ScrollToTopOnPageChange = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
};

type PageLevelErrorProps = {
  message: React.ReactNode;
  timeoutMs?: number | null;
};

const DEFAULT_TIMEOUT_MS = 3000;

let lastError: PageLevelErrorProps | null = null;

export const showPageLevelError = (props: PageLevelErrorProps) => {
  lastError = props;
  triggerRender();
};

let triggerRender = () => {};

export const PageLevelError = () => {
  const [isOpen, setIsOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setRenderTrigger] = useState({});
  triggerRender = () => setRenderTrigger({});

  useEffect(() => {
    if (lastError) {
      setIsOpen(true);
    }
  }, [lastError]);

  const onClose = () => {
    setIsOpen(false);
  };

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={isOpen}
      onClose={onClose}
      autoHideDuration={lastError?.timeoutMs ?? DEFAULT_TIMEOUT_MS}
      message={lastError?.message}
      ContentProps={{
        sx: {
          backgroundColor: 'error.main',
          color: 'error.contrastText',
        },
      }}
    />
  );
};

export const FadedScrollerContainer = styled('div')`
  height: 100%;
  position: relative;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    // &::after {
    //   background: rgba(10, 43, 53);
    //   background: linear-gradient(0deg, #032629 25%, rgba(10, 43, 53, 0) 100%);
    //   bottom: 0;
    //   content: '';
    //   height: ${({ theme }) => theme.spacing(3)};
    //   position: absolute;
    //   touch-action: none;
    //   width: calc(100% - 16px);
    // }

    // &::before {
    //   background: rgb(10, 43, 53);
    //   background: linear-gradient(180deg, #032629 25%, rgba(10, 43, 53, 0) 100%);
    //   content: '';
    //   height: ${({ theme }) => theme.spacing(7.5)};
    //   position: absolute;
    //   top: 0;
    //   touch-action: none;
    //   width: calc(100% - 16px);
    //   z-index: 1;
    // }
  }
`;

export const scrollbarStyles = css`
  /* Firefox */
  & {
    scrollbar-width: auto;
    scrollbar-color: #0a3235 #135458;
  }

  /* Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    width: 16px;
  }
  &::-webkit-scrollbar-track {
    background: #135458;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #0a3235;
    border-radius: 10px;
    border: 3px solid #ffffff;
  }
`;

export const Scroller = styled('div')`
  inset: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(1.5)};
  padding-top: ${({ theme }) => theme.spacing(2)};
  position: absolute;

  ${scrollbarStyles};

  ${({ theme }) => theme.breakpoints.up('lg')} {
    padding: ${({ theme }) => theme.spacing(3)};

    // leave room at the top when inside a FadedScrollerContainer or the fade will cover contents
    ${FadedScrollerContainer} & {
      padding-top: ${({ theme }) => theme.spacing(6)};
    }
  }
`;

export function getThreadPath(thread: Thread | null): string {
  if (thread) {
    return `/chat/${thread.id}`;
  }
  const handlerProfile = localStorage.getItem('defaultHandlerProfile');
  if (handlerProfile) {
    return `/chat?profile=${handlerProfile}`;
  }
  return '/chat';
}

export const useNavigateToChatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (thread: Thread | null) => {
      const path = getThreadPath(thread);
      if (location.pathname === path) {
        return;
      }
      navigate(path);
    },
    [location.pathname, navigate],
  );
};
